/**
 * PIP-382 — does a venue box-office URL actually belong to the event's venue?
 *
 * Ticketmaster publishes a `venueBoxOffice` outlet per event as a bare
 * `{ type, url }`: no venue id, no address, no coordinates. On 2026-10-03 two
 * Charlotte resale events whose TM venue was the right room (same TM venue id,
 * same coordinates as the LOCI location) carried the box office of a venue in
 * Savannah and one in Indianapolis. A venue-id or distance check passes those
 * rows, because the venue is right and only the outlet is wrong. The one thing
 * that ties the outlet to a venue is its host, so the guard corroborates the
 * host against the venue itself:
 *
 *   1. the host is the venue's curated website host (or a subdomain of it,
 *      either way round — `tix.example.org` vs `www.example.org`); or
 *   2. the host names the venue: its label contains a distinctive word of the
 *      venue name (`amossouthend.com` for "Amos Southend").
 *
 * Anything else is refused. A refused correct outlet costs a fallback CTA
 * (venue website, else "Check resale on Ticketmaster"); an accepted wrong one
 * sends a buyer to another city. The guard is therefore strict, and a venue
 * whose box office lives on an operator's domain (e.g. a performing-arts group
 * running several rooms) needs its `Location.websiteUrl` curated to pass.
 *
 * Shared by the TM ingest + backfill (data-pipeline), which refuse to persist a
 * mismatched outlet, and the CTA derivation (backend-api), which refuses to
 * send a buyer to one already stored.
 */

/** What the guard knows about the event's venue. */
export interface BoxOfficeVenue {
  /** The venue's display name (`Location.name`, or TM's embedded venue name). */
  readonly name?: string | null;
  /** The venue's city. Its words never count as distinctive. */
  readonly city?: string | null;
  /** The venue's curated website (`Location.websiteUrl`). */
  readonly websiteUrl?: string | null;
}

/** Shortest venue-name word that may corroborate a host on its own. */
const MIN_DISTINCTIVE_TOKEN_LENGTH = 4;

/**
 * Words too generic to tie a host to ONE venue: room types, genres, common
 * place words. Matching on any of them would let `jazzclubsavannah.com`
 * corroborate "Middle C Jazz".
 */
const GENERIC_VENUE_WORDS: ReadonlySet<string> = new Set([
  'amphitheater',
  'amphitheatre',
  'arena',
  'arts',
  'auditorium',
  'ballroom',
  'band',
  'blues',
  'brewery',
  'brewing',
  'cafe',
  'center',
  'centre',
  'church',
  'city',
  'club',
  'comedy',
  'company',
  'concert',
  'concerts',
  'county',
  'downtown',
  'east',
  'event',
  'events',
  'field',
  'garden',
  'gardens',
  'grill',
  'hall',
  'house',
  'jazz',
  'live',
  'lounge',
  'museum',
  'music',
  'north',
  'park',
  'pavilion',
  'place',
  'plaza',
  'room',
  'rock',
  'saloon',
  'social',
  'south',
  'square',
  'stadium',
  'stage',
  'street',
  'studio',
  'studios',
  'tavern',
  'theater',
  'theaters',
  'theatre',
  'theatres',
  'tickets',
  'uptown',
  'venue',
  'west',
]);

/** Lower-case, strip diacritics and apostrophes, split on anything else non-alphanumeric. */
function words(value: string): string[] {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 0);
}

/**
 * The words of a venue name that can corroborate a host on their own:
 * at least {@link MIN_DISTINCTIVE_TOKEN_LENGTH} characters, not generic, and
 * not part of the city's name. Order-preserving and de-duplicated.
 */
export function venueNameTokens(
  name: string | null | undefined,
  city?: string | null,
): readonly string[] {
  if (!name) return [];
  const cityWords = new Set(city ? words(city) : []);
  const tokens: string[] = [];
  for (const word of words(name)) {
    if (word.length < MIN_DISTINCTIVE_TOKEN_LENGTH) continue;
    if (GENERIC_VENUE_WORDS.has(word) || cityWords.has(word)) continue;
    if (!tokens.includes(word)) tokens.push(word);
  }
  return tokens;
}

/** Lower-cased host of an `http(s):` URL without a leading `www.`, or `null`. */
function webHost(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  return parsed.hostname.toLowerCase().replace(/^www\./, '') || null;
}

/** Whether two hosts are one site: equal, or one a subdomain of the other. */
function sameSite(a: string, b: string): boolean {
  return a === b || a.endsWith(`.${b}`) || b.endsWith(`.${a}`);
}

/**
 * The host without its last label (the TLD), letters and digits only, so a
 * venue word can never match on the TLD alone.
 */
function hostLabel(host: string): string {
  const labels = host.split('.');
  const named = labels.length > 1 ? labels.slice(0, -1) : labels;
  return named.join('').replace(/[^a-z0-9]/g, '');
}

/**
 * Whether `url` is a box office the event's `venue` can vouch for. See the
 * module doc for the rule and why it is host-based. `false` for anything
 * unusable (not `http(s):`, unparseable, empty).
 */
export function isBoxOfficeUrlForVenue(
  url: string | null | undefined,
  venue: BoxOfficeVenue,
): boolean {
  const host = webHost(url);
  if (host === null) return false;

  const websiteHost = webHost(venue.websiteUrl);
  if (websiteHost !== null && sameSite(host, websiteHost)) return true;

  const label = hostLabel(host);
  return venueNameTokens(venue.name, venue.city).some((token) => label.includes(token));
}
